import { defineComponent as _defineComponent } from 'vue'
import { withModifiers as _withModifiers, createElementVNode as _createElementVNode, createVNode as _createVNode, renderSlot as _renderSlot, unref as _unref, normalizeClass as _normalizeClass, toDisplayString as _toDisplayString, withCtx as _withCtx, resolveDirective as _resolveDirective, openBlock as _openBlock, createElementBlock as _createElementBlock, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = ["cell-id"]
const _hoisted_2 = { class: "drag-handle" }
const _hoisted_3 = { class: "cell-contents" }
const _hoisted_4 = {
  class: "menu-area",
  ref: "menuAreaRef"
}
const _hoisted_5 = { class: "cell-type-dropdown-item" }
const _hoisted_6 = { class: "extra-area" }

import { ref, inject, computed } from "vue";
import Button from 'primevue/button';
import DraggableMarker from './DraggableMarker.vue';
import { type BeakerNotebookComponentType } from '../notebook/BeakerNotebook.vue';

import { type IBeakerCell } from "beaker-kernel/src";
import Dropdown from 'primevue/dropdown';
import OverlayPanel from 'primevue/overlaypanel';
import { BeakerSessionComponentType } from "../session/BeakerSession.vue";

export interface Props {
    cell: IBeakerCell;
    index?: number;
    dragEnabled?: boolean;
}

enum CellState {
    Success = 'success',
    Modified = 'modified',
    Error = 'error',
    Pending = 'pending',
}


export default /*@__PURE__*/_defineComponent({
  __name: 'BeakerCell',
  props: {
    cell: {},
    index: {},
    dragEnabled: { type: Boolean }
  },
  emits: [
    'move-cell',
],
  setup(__props: any, { emit: __emit }) {

const props = __props;
const emit = __emit;

const beakerSession = inject<BeakerSessionComponentType>('beakerSession');
const notebook = inject<BeakerNotebookComponentType>("notebook");
const cellMap = inject("cell-component-mapping");

const clicked = (evt) => {
    if (!evt.defaultPrevented) {
        notebook.selectCell(props.cell as {id: string}, );
    }
};

const beakerCellRef = ref<HTMLDivElement|null>(null);
const hoverMenuRef = ref(null);
const cellMenuButton = ref(null);
const metadata = ref(props.cell.metadata);
const collapsed = computed(() => {
    return metadata.value?.collapsed || false;
});

const cellIconMap = {
    "code": "pi pi-code",
    "markdown": "pi pi-pencil",
    "query": "pi pi-sparkles",
    "raw": "pi pi-question-circle",
};

const cellIcon = computed(() => cellIconMap[props.cell.cell_type]);

const cellState = ref<CellState>(CellState.Pending);

const collapseCell = (evt) => {
    if (metadata.value) {
        metadata.value.collapsed = !collapsed.value;
    }
};

const moveUp = () => {
    if (props.index > 0) {
        emit("move-cell", props.index, props.index - 1);
    }
}

const moveDown = () => {
    if (props.index < notebook.cellCount - 1) {
        emit("move-cell", props.index, props.index + 1);
    }
}

const overlayMenuHoverHandler = (event: PointerEvent) => {
    // Trigger if we are entering, but not moving between elements with the specified classes
    if (!((event?.target instanceof HTMLElement) && (event?.relatedTarget instanceof HTMLElement))) {
        return;
    }
    const selectors = [
        `[cell-id="${props.cell.id}"] .menu-area`,
        `[cell-id="${props.cell.id}"] .hover-menu`,
        `.menu-overlay[cell-id="${props.cell.id}"]`,
    ];
    const selectorString = selectors.join(', ');
    const closest = event.relatedTarget.closest(selectorString);
    const ignore = closest !== null;
    if (!ignore) {
        if (event.type.toLowerCase().includes('enter')) {
            hoverMenuRef.value.show(event, cellMenuButton.value.$el);
        }
        else if (event.type.toLowerCase().includes('leave')) {
            hoverMenuRef.value.hide();
        }
    }
}


return (_ctx: any,_cache: any) => {
  const _directive_tooltip = _resolveDirective("tooltip")!

  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["beaker-cell", {collapsed: collapsed.value}]),
    tabindex: "0",
    onClick: clicked,
    ref_key: "beakerCellRef",
    ref: beakerCellRef,
    "cell-id": _ctx.cell.id
  }, [
    _createElementVNode("div", {
      class: "collapse-box",
      onClick: _withModifiers(collapseCell, ["prevent"])
    }),
    _createElementVNode("div", _hoisted_2, [
      _createVNode(DraggableMarker, {
        draggable: props.dragEnabled
      }, null, 8, ["draggable"])
    ]),
    _createElementVNode("div", _hoisted_3, [
      _renderSlot(_ctx.$slots, "default"),
      _renderSlot(_ctx.$slots, "child-cells")
    ]),
    _createElementVNode("div", _hoisted_4, [
      _renderSlot(_ctx.$slots, "hover-menu", {}, () => [
        _createElementVNode("div", {
          class: "hover-menu",
          onPointerenter: overlayMenuHoverHandler,
          onPointerleave: overlayMenuHoverHandler
        }, [
          _createVNode(_unref(Button), {
            ref_key: "cellMenuButton",
            ref: cellMenuButton,
            class: "cell-menu-button",
            text: "",
            small: "",
            icon: "pi pi-ellipsis-v",
            onClick: _cache[0] || (_cache[0] = _withModifiers(($event: any) => {hoverMenuRef.value.show($event);}, ["prevent"]))
          }, null, 512),
          _createVNode(_unref(OverlayPanel), {
            class: "menu-overlay",
            ref_key: "hoverMenuRef",
            ref: hoverMenuRef,
            "cell-id": _ctx.cell.id,
            onPointerenter: overlayMenuHoverHandler,
            onPointerleave: overlayMenuHoverHandler
          }, {
            default: _withCtx(() => [
              _withDirectives((_openBlock(), _createElementBlock("div", null, [
                _createVNode(_unref(Dropdown), {
                  name: `${_ctx.cell.id}-celltype`,
                  class: "cell-type-selector overlay-menu-button",
                  "model-value": _ctx.cell.cell_type,
                  "onUpdate:modelValue": _cache[1] || (_cache[1] = (value) => {_unref(notebook).convertCellType(_ctx.cell, value); hoverMenuRef.value.hide();}),
                  options: Object.keys(_unref(cellMap) || {}),
                  "dropdown-icon": cellIcon.value,
                  "append-to": "self"
                }, {
                  value: _withCtx((slotProps) => [
                    _createElementVNode("span", {
                      class: _normalizeClass(cellIconMap[slotProps.value])
                    }, null, 2)
                  ]),
                  option: _withCtx((slotProps) => [
                    _createElementVNode("div", _hoisted_5, [
                      _createElementVNode("span", {
                        class: _normalizeClass(cellIconMap[slotProps.option])
                      }, null, 2),
                      _createElementVNode("span", null, _toDisplayString(slotProps.option), 1)
                    ])
                  ]),
                  _: 1
                }, 8, ["name", "model-value", "options", "dropdown-icon"])
              ])), [
                [
                  _directive_tooltip,
                  'Change Cell Type',
                  void 0,
                  { left: true }
                ]
              ]),
              _withDirectives(_createVNode(_unref(Button), {
                onClick: _cache[2] || (_cache[2] = ($event: any) => {_unref(beakerSession).findNotebookCellById(_ctx.cell.id).execute(); hoverMenuRef.value.hide();}),
                icon: "pi pi-play",
                size: "small",
                text: ""
              }, null, 512), [
                [
                  _directive_tooltip,
                  'Execute cell',
                  void 0,
                  { left: true }
                ]
              ]),
              _withDirectives(_createVNode(_unref(Button), {
                onClick: _cache[3] || (_cache[3] = ($event: any) => {_unref(notebook).removeCell(_unref(beakerSession).findNotebookCellById(_ctx.cell.id)); hoverMenuRef.value.hide();}),
                icon: "pi pi-minus",
                size: "small",
                text: ""
              }, null, 512), [
                [
                  _directive_tooltip,
                  'Remove cell',
                  void 0,
                  { left: true }
                ]
              ]),
              _withDirectives(_createVNode(_unref(Button), {
                onClick: _cache[4] || (_cache[4] = ($event: any) => {moveUp(); hoverMenuRef.value.hide();}),
                icon: "pi pi-chevron-up",
                size: "small",
                text: ""
              }, null, 512), [
                [
                  _directive_tooltip,
                  'Move cell up',
                  void 0,
                  { left: true }
                ]
              ]),
              _withDirectives(_createVNode(_unref(Button), {
                onClick: _cache[5] || (_cache[5] = ($event: any) => {moveDown(); hoverMenuRef.value.hide();}),
                icon: "pi pi-chevron-down",
                size: "small",
                text: ""
              }, null, 512), [
                [
                  _directive_tooltip,
                  'Move cell down',
                  void 0,
                  { left: true }
                ]
              ])
            ]),
            _: 1
          }, 8, ["cell-id"])
        ], 32)
      ])
    ], 512),
    _createElementVNode("div", _hoisted_6, [
      _renderSlot(_ctx.$slots, "extra")
    ])
  ], 10, _hoisted_1))
}
}

})